import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  Plus, 
  Search, 
  Filter, 
  Download, 
  Eye, 
  Edit, 
  Trash2,
  DollarSign,
  TrendingUp,
  TrendingDown,
  Calendar,
  FileText,
  BarChart3,
  RefreshCw,
  Zap,
  Target,
  Activity,
  CheckCircle,
  Clock,
  XCircle,
  X,
  Save
} from 'lucide-react';
import { toast } from 'react-hot-toast';
import { useAuth } from '../../contexts/AuthContext';
import { 
  formatNumberWithCommas, 
  parseNumberFromFormatted, 
  numberToVietnameseText,
  numberToEnglishText
} from '../../utils/currencyUtils';

const AccountantAddFinance = () => {
  const { user } = useAuth();
  const [financeRecords, setFinanceRecords] = useState([]);

  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [dateFilter, setDateFilter] = useState('all');
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');
  const [showCustomDateInputs, setShowCustomDateInputs] = useState(false);
  const [currencyFilter, setCurrencyFilter] = useState('all');
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showReportModal, setShowReportModal] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [editingRecord, setEditingRecord] = useState(null);
  const [cancellingRecord, setCancellingRecord] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [statistics, setStatistics] = useState({
    totalIncome: 0,
    totalExpense: 0,
    netProfit: 0,
    currencyStats: {
      VND: { totalIncome: 0, totalExpense: 0, netProfit: 0 },
      USD: { totalIncome: 0, totalExpense: 0, netProfit: 0 }
    }
  });
  const [chartData, setChartData] = useState(null);
  const [chartTitle, setChartTitle] = useState('Thống kê thu chi theo ngày');
  const [chartCurrency, setChartCurrency] = useState('VND');

  // Form data for adding new record
  const [formData, setFormData] = useState({
    title: '',
    type: 'thu',
    amount: '',
    currency: 'VND',
    content: '',
    date: new Date().toISOString().split('T')[0]
  });
  const [amountInWords, setAmountInWords] = useState('');
  const [dynamicSuggestions, setDynamicSuggestions] = useState([]);

  // Filter states
  const [selectedType, setSelectedType] = useState('all');
  const [selectedDateRange, setSelectedDateRange] = useState('all');

  // Tạo gợi ý linh động dựa trên số đã nhập
  const generateDynamicSuggestions = (inputValue) => {
    if (!inputValue || inputValue.length === 0) {
      setDynamicSuggestions([]);
      return;
    }
    
    const numericValue = parseInt(inputValue.replace(/,/g, ''));
    if (isNaN(numericValue) || numericValue === 0) {
      setDynamicSuggestions([]);
      return;
    }
    
    // Tạo các gợi ý với các bội số khác nhau
    const suggestions = [];
    
    // Gợi ý thông minh dựa trên độ dài số
    const numLength = numericValue.toString().length;
    
    if (numLength <= 2) {
      // Số 1-2 chữ số: gợi ý nhân với 1000, 10000, 100000, 1000000
      const multipliers = [1000, 10000, 100000, 1000000];
      multipliers.forEach(multiplier => {
        const suggestion = numericValue * multiplier;
        if (suggestion <= 1000000000) {
          suggestions.push(suggestion);
        }
      });
    } else if (numLength === 3) {
      // Số 3 chữ số: gợi ý nhân với 100, 1000, 10000
      const multipliers = [100, 1000, 10000];
      multipliers.forEach(multiplier => {
        const suggestion = numericValue * multiplier;
        if (suggestion <= 1000000000) {
          suggestions.push(suggestion);
        }
      });
    } else if (numLength === 4) {
      // Số 4 chữ số: gợi ý nhân với 10, 100, 1000
      const multipliers = [10, 100, 1000];
      multipliers.forEach(multiplier => {
        const suggestion = numericValue * multiplier;
        if (suggestion <= 1000000000) {
          suggestions.push(suggestion);
        }
      });
    } else {
      // Số lớn hơn: gợi ý nhân với 10, 100
      const multipliers = [10, 100];
      multipliers.forEach(multiplier => {
        const suggestion = numericValue * multiplier;
        if (suggestion <= 1000000000) {
          suggestions.push(suggestion);
        }
      });
    }
    
    // Thêm gợi ý làm tròn lên đến các mốc đẹp
    const roundUpSuggestions = [];
    const roundTargets = [1000, 5000, 10000, 50000, 100000, 500000, 1000000, 5000000, 10000000];
    
    roundTargets.forEach(target => {
      if (numericValue < target && target <= numericValue * 10) {
        roundUpSuggestions.push(target);
      }
    });
    
    // Kết hợp tất cả gợi ý
    const allSuggestions = [...suggestions, ...roundUpSuggestions];
    
    // Loại bỏ các giá trị trùng lặp và sắp xếp
    const uniqueSuggestions = [...new Set(allSuggestions)].sort((a, b) => a - b);
    
    // Giới hạn tối đa 6 gợi ý
    setDynamicSuggestions(uniqueSuggestions.slice(0, 6));
  };

  // Categories for income and expense
  const incomeCategories = [
    'Lương cơ bản',
    'Thưởng',
    'Phụ cấp',
    'Thu nhập khác',
    'Đầu tư',
    'Kinh doanh'
  ];

  const expenseCategories = [
    'Ăn uống',
    'Di chuyển',
    'Nhà ở',
    'Tiện ích',
    'Giải trí',
    'Mua sắm',
    'Y tế',
    'Giáo dục',
    'Khác'
  ];





  useEffect(() => {
    // Load local data if available
    const savedRecords = localStorage.getItem('accountantFinanceRecords');
    if (savedRecords) {
      setFinanceRecords(JSON.parse(savedRecords));
    }
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    if (name === 'amount') {
      const currentCurrency = formData.currency || 'VND';
      let cleanValue, formattedValue, numericValue;
      
      if (currentCurrency === 'USD') {
        // Cho USD: cho phép số, dấu chấm thập phân, và dấu phẩy ngăn cách hàng nghìn
        cleanValue = value.replace(/[^\d.,]/g, '');
        
        // Format số với dấu phẩy ngăn cách hàng nghìn và dấu chấm thập phân
        formattedValue = formatNumberWithCommas(cleanValue, 'USD');
        numericValue = parseNumberFromFormatted(formattedValue, 'USD');
      } else {
        // Cho VND: chỉ cho phép số và dấu phẩy
        cleanValue = value.replace(/[^\d,]/g, '');
        
        // Format số với dấu phẩy
        formattedValue = formatNumberWithCommas(cleanValue, 'VND');
        numericValue = parseNumberFromFormatted(formattedValue, 'VND');
      }
      
      setFormData(prev => ({
        ...prev,
        [name]: formattedValue
      }));
      
      // Cập nhật chữ tiền theo loại tiền tệ
      if (numericValue > 0) {
        if (currentCurrency === 'USD') {
          setAmountInWords(numberToEnglishText(numericValue));
        } else {
          setAmountInWords(numberToVietnameseText(numericValue));
        }
      } else {
        setAmountInWords('');
      }
      
      // Tạo gợi ý linh động
      generateDynamicSuggestions(cleanValue);
    } else if (name === 'currency') {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
      // Cập nhật lại chữ tiền khi đổi loại tiền tệ
      if (formData.amount) {
        const numericValue = parseNumberFromFormatted(formData.amount);
        if (numericValue > 0) {
          if (value === 'USD') {
            setAmountInWords(numberToEnglishText(numericValue));
          } else {
            setAmountInWords(numberToVietnameseText(numericValue));
          }
        }
      }
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  const handleAddRecord = () => {
    setShowAddModal(true);
    setFormData({
      title: '',
      type: 'thu',
      amount: '',
      currency: 'VND',
      content: '',
      date: new Date().toISOString().split('T')[0]
    });
    setAmountInWords('');
    setDynamicSuggestions([]);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      setLoading(true);
      
      // Validation
      if (!formData.title || !formData.amount || !formData.content) {
        toast.error('Vui lòng điền đầy đủ thông tin!');
        return;
      }

      const numericAmount = parseNumberFromFormatted(formData.amount, formData.currency);
      if (numericAmount <= 0) {
        toast.error('Số tiền phải lớn hơn 0!');
        return;
      }

      const newRecord = {
        id: Date.now() + Math.random(),
        type: formData.type === 'thu' ? 'income' : 'expense',
        title: formData.title,
        amount: numericAmount,
        currency: formData.currency,
        description: formData.content,
        date: formData.date,
        created_at: new Date().toISOString(),
        accountant_id: user?.id || 'local'
      };

      // For now, save locally
      const updatedRecords = [...financeRecords, newRecord];
      setFinanceRecords(updatedRecords);
      localStorage.setItem('accountantFinanceRecords', JSON.stringify(updatedRecords));
      
      toast.success('Thêm thu chi thành công!');
      setShowAddModal(false);
    } catch (error) {
      console.error('Error adding record:', error);
      toast.error('Có lỗi xảy ra khi thêm thu chi!');
    } finally {
      setLoading(false);
    }
  };

  const handleEditRecord = (record) => {
    setEditingRecord({
      ...record,
      amount: formatNumberWithCommas(record.amount.toString())
    });
    setShowEditModal(true);
  };

  const handleSubmitEdit = async (e) => {
    e.preventDefault();
    
    if (!editingRecord.amount || !editingRecord.description || !editingRecord.category) {
      toast.error('Vui lòng điền đầy đủ thông tin!');
      return;
    }

    const amount = parseFloat(editingRecord.amount.replace(/,/g, ''));
    if (isNaN(amount) || amount <= 0) {
      toast.error('Số tiền không hợp lệ!');
      return;
    }

    try {
      const updatedRecord = {
        ...editingRecord,
        amount: amount
      };

      const updatedRecords = financeRecords.map(record => 
        record.id === editingRecord.id ? updatedRecord : record
      );
      
      setFinanceRecords(updatedRecords);
      localStorage.setItem('accountantFinanceRecords', JSON.stringify(updatedRecords));
      
      toast.success('Cập nhật thu chi thành công!');
      setShowEditModal(false);
      setEditingRecord(null);
    } catch (error) {
      console.error('Error updating record:', error);
      toast.error('Có lỗi xảy ra khi cập nhật thu chi!');
    }
  };

  const handleDeleteRecord = (recordId) => {
    if (window.confirm('Bạn có chắc muốn xóa bản ghi này?')) {
      const updatedRecords = financeRecords.filter(record => record.id !== recordId);
      setFinanceRecords(updatedRecords);
      localStorage.setItem('accountantFinanceRecords', JSON.stringify(updatedRecords));
      toast.success('Xóa thu chi thành công!');
    }
  };

  const formatNumberWithCommas = (num) => {
    return num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  };

  const formatCurrency = (amount, currency = 'VND') => {
    if (currency === 'USD') {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD'
      }).format(amount);
    }
    
    return new Intl.NumberFormat('vi-VN', {
      style: 'currency',
      currency: 'VND'
    }).format(amount);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('vi-VN');
  };

  const filterRecords = () => {
    let filtered = [...financeRecords];

    // Filter by search term
    if (searchTerm) {
      filtered = filtered.filter(record => 
        record.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        record.description?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Filter by type
    if (selectedType !== 'all') {
      filtered = filtered.filter(record => record.type === selectedType);
    }

    // Filter by status
    if (statusFilter !== 'all') {
      filtered = filtered.filter(record => record.status === statusFilter);
    }

    // Filter by currency
    if (currencyFilter !== 'all') {
      filtered = filtered.filter(record => record.currency === currencyFilter);
    }

    // Filter by date range
    if (dateFilter !== 'all') {
      const today = new Date();
      const startDate = new Date();
      
      switch (dateFilter) {
        case 'today':
          startDate.setHours(0, 0, 0, 0);
          filtered = filtered.filter(record => {
            const recordDate = new Date(record.date);
            return recordDate >= startDate && recordDate <= today;
          });
          break;
        case 'week':
          startDate.setDate(today.getDate() - 7);
          filtered = filtered.filter(record => {
            const recordDate = new Date(record.date);
            return recordDate >= startDate;
          });
          break;
        case 'month':
          startDate.setMonth(today.getMonth() - 1);
          filtered = filtered.filter(record => {
            const recordDate = new Date(record.date);
            return recordDate >= startDate;
          });
          break;
        case 'year':
          startDate.setFullYear(today.getFullYear() - 1);
          filtered = filtered.filter(record => {
            const recordDate = new Date(record.date);
            return recordDate >= startDate;
          });
          break;
        case 'custom':
          if (customStartDate && customEndDate) {
            const start = new Date(customStartDate);
            const end = new Date(customEndDate);
            filtered = filtered.filter(record => {
              const recordDate = new Date(record.date);
              return recordDate >= start && recordDate <= end;
            });
          }
          break;
      }
    }

    return filtered;
  };

  const filteredRecords = filterRecords();

  // Tính toán thống kê theo từng loại tiền tệ
  const statsByCurrency = {};
  
  filteredRecords.forEach(record => {
    const currency = record.currency || 'VND';
    if (!statsByCurrency[currency]) {
      statsByCurrency[currency] = { income: 0, expense: 0 };
    }
    
    if (record.type === 'income') {
      statsByCurrency[currency].income += record.amount;
    } else {
      statsByCurrency[currency].expense += record.amount;
    }
  });

  // Tính tổng cho VND và USD
  const vndStats = statsByCurrency['VND'] || { income: 0, expense: 0 };
  const usdStats = statsByCurrency['USD'] || { income: 0, expense: 0 };
  
  const vndBalance = vndStats.income - vndStats.expense;
  const usdBalance = usdStats.income - usdStats.expense;

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed':
      case 'approved':
        return 'bg-green-100 text-green-800';
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      case 'rejected':
        return 'bg-red-100 text-red-800';
      case 'cancelled':
        return 'bg-gray-100 text-gray-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getTypeColor = (type) => {
    return type === 'income' ? 'text-green-600' : 'text-red-600';
  };

  const getTypeIcon = (type) => {
    return type === 'income' ? <TrendingUp className="w-4 h-4" /> : <TrendingDown className="w-4 h-4" />;
  };

  const handleCustomDateFilter = () => {
    if (customStartDate && customEndDate) {
      setDateFilter('custom');
      setShowCustomDateInputs(false);
    } else {
      toast.error('Vui lòng chọn cả ngày bắt đầu và ngày kết thúc');
    }
  };

  const handleDateFilterChange = (e) => {
    const value = e.target.value;
    setDateFilter(value);
    if (value !== 'custom') {
      setShowCustomDateInputs(false);
      setCustomStartDate('');
      setCustomEndDate('');
    } else {
      setShowCustomDateInputs(true);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 p-4 sm:p-6 lg:p-8">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="max-w-7xl mx-auto"
      >
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Thêm Thu Chi</h1>
          <p className="text-gray-600">Quản lý thu chi cá nhân của kế toán</p>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-6 gap-4 mb-8">
          {/* VND Stats */}
          <motion.div
            whileHover={{ scale: 1.02 }}
            className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 md:col-span-3"
          >
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Thống kê VND</h3>
              <span className="text-sm font-medium text-gray-500">VNĐ</span>
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div className="text-center">
                <div className="p-2 bg-green-100 rounded-lg mx-auto w-12 h-12 flex items-center justify-center mb-2">
                  <TrendingUp className="w-5 h-5 text-green-600" />
                </div>
                <p className="text-xs font-medium text-gray-600">Thu</p>
                <p className="text-lg font-bold text-green-600">
                  {formatCurrency(vndStats.income, 'VND')}
                </p>
              </div>
              <div className="text-center">
                <div className="p-2 bg-red-100 rounded-lg mx-auto w-12 h-12 flex items-center justify-center mb-2">
                  <TrendingDown className="w-5 h-5 text-red-600" />
                </div>
                <p className="text-xs font-medium text-gray-600">Chi</p>
                <p className="text-lg font-bold text-red-600">
                  {formatCurrency(vndStats.expense, 'VND')}
                </p>
              </div>
              <div className="text-center">
                <div className="p-2 bg-blue-100 rounded-lg mx-auto w-12 h-12 flex items-center justify-center mb-2">
                  <DollarSign className="w-5 h-5 text-blue-600" />
                </div>
                <p className="text-xs font-medium text-gray-600">Số dư</p>
                <p className={`text-lg font-bold ${vndBalance >= 0 ? 'text-blue-600' : 'text-red-600'}`}>
                  {formatCurrency(vndBalance, 'VND')}
                </p>
              </div>
            </div>
          </motion.div>

          {/* USD Stats */}
          <motion.div
            whileHover={{ scale: 1.02 }}
            className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 md:col-span-3"
          >
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Thống kê USD</h3>
              <span className="text-sm font-medium text-gray-500">$</span>
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div className="text-center">
                <div className="p-2 bg-green-100 rounded-lg mx-auto w-12 h-12 flex items-center justify-center mb-2">
                  <TrendingUp className="w-5 h-5 text-green-600" />
                </div>
                <p className="text-xs font-medium text-gray-600">Thu</p>
                <p className="text-lg font-bold text-green-600">
                  {formatCurrency(usdStats.income, 'USD')}
                </p>
              </div>
              <div className="text-center">
                <div className="p-2 bg-red-100 rounded-lg mx-auto w-12 h-12 flex items-center justify-center mb-2">
                  <TrendingDown className="w-5 h-5 text-red-600" />
                </div>
                <p className="text-xs font-medium text-gray-600">Chi</p>
                <p className="text-lg font-bold text-red-600">
                  {formatCurrency(usdStats.expense, 'USD')}
                </p>
              </div>
              <div className="text-center">
                <div className="p-2 bg-blue-100 rounded-lg mx-auto w-12 h-12 flex items-center justify-center mb-2">
                  <DollarSign className="w-5 h-5 text-blue-600" />
                </div>
                <p className="text-xs font-medium text-gray-600">Số dư</p>
                <p className={`text-lg font-bold ${usdBalance >= 0 ? 'text-blue-600' : 'text-red-600'}`}>
                  {formatCurrency(usdBalance, 'USD')}
                </p>
              </div>
            </div>
          </motion.div>
        </div>

        {/* Controls */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-8">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
            <div className="flex flex-col sm:flex-row gap-4">
              {/* Type Filter */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Loại</label>
                <select
                  value={selectedType}
                  onChange={(e) => setSelectedType(e.target.value)}
                  className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="all">Tất cả</option>
                  <option value="income">Thu</option>
                  <option value="expense">Chi</option>
                </select>
              </div>

              {/* Date Filter */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Thời gian</label>
                <select
                  value={selectedDateRange}
                  onChange={(e) => {
                    setSelectedDateRange(e.target.value);
                    if (e.target.value === 'custom') {
                      setShowCustomDateInputs(true);
                    } else {
                      setShowCustomDateInputs(false);
                    }
                  }}
                  className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="all">Tất cả</option>
                  <option value="today">Hôm nay</option>
                  <option value="week">Tuần này</option>
                  <option value="month">Tháng này</option>
                  <option value="year">Năm nay</option>
                  <option value="custom">Tùy chỉnh</option>
                </select>
              </div>
            </div>

            {/* Add Button */}
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={handleAddRecord}
              className="flex items-center px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              <Plus className="w-5 h-5 mr-2" />
              Thêm Thu Chi
            </motion.button>
          </div>

          {/* Custom Date Inputs */}
          {showCustomDateInputs && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-4"
            >
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Từ ngày</label>
                <input
                  type="date"
                  value={customStartDate}
                  onChange={(e) => setCustomStartDate(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Đến ngày</label>
                <input
                  type="date"
                  value={customEndDate}
                  onChange={(e) => setCustomEndDate(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
            </motion.div>
          )}
        </div>

        {/* Records List */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900">Danh sách thu chi ({filteredRecords.length})</h3>
          </div>
          
          {filteredRecords.length === 0 ? (
            <div className="p-8 text-center">
              <FileText className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-500">Chưa có bản ghi thu chi nào</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Ngày</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Loại</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Danh mục</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Mô tả</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Số tiền</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Thao tác</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredRecords.map((record) => (
                    <motion.tr
                      key={record.id}
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      whileHover={{ backgroundColor: '#f9fafb' }}
                      className="hover:bg-gray-50"
                    >
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {new Date(record.date).toLocaleDateString('vi-VN')}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                          record.type === 'income' 
                            ? 'bg-green-100 text-green-800' 
                            : 'bg-red-100 text-red-800'
                        }`}>
                          {record.type === 'income' ? 'Thu' : 'Chi'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {record.category}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-900 max-w-xs truncate">
                        {record.description}
                      </td>
                                             <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                         <span className={record.type === 'income' ? 'text-green-600' : 'text-red-600'}>
                           {formatCurrency(record.amount, record.currency)}
                         </span>
                         <span className="text-xs text-gray-500 ml-1">({record.currency})</span>
                       </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <div className="flex space-x-2">
                          <button
                            onClick={() => handleEditRecord(record)}
                            className="text-blue-600 hover:text-blue-900"
                          >
                            Sửa
                          </button>
                          <button
                            onClick={() => handleDeleteRecord(record.id)}
                            className="text-red-600 hover:text-red-900"
                          >
                            Xóa
                          </button>
                        </div>
                      </td>
                    </motion.tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </motion.div>

             {/* Add Modal */}
       {showAddModal && (
         <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
           <div className="w-full max-w-md shadow-2xl rounded-2xl bg-white animate-scaleIn max-h-[90vh] overflow-y-auto">
             <div className="p-4">
                             <div className="flex items-center justify-between mb-4">
                 <h3 className="text-xl font-bold text-gray-900">Thêm báo cáo tài chính</h3>
                 <button
                   onClick={() => setShowAddModal(false)}
                   className="p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-all duration-200"
                 >
                   <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                     <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                   </svg>
                 </button>
               </div>

                             <form onSubmit={handleSubmit} className="space-y-3">
                                 <div>
                   <label className="block text-sm font-semibold text-gray-700 mb-1">Tiêu đề</label>
                                     <input
                     type="text"
                     name="title"
                     value={formData.title}
                     onChange={handleInputChange}
                     className="w-full px-3 py-2 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-all duration-200"
                     placeholder="Nhập tiêu đề báo cáo"
                     required
                   />
                </div>
                                 <div className="grid grid-cols-2 gap-4">
                   <div>
                     <label className="block text-sm font-semibold text-gray-700 mb-1">Loại</label>
                                        <select 
                        name="type"
                        value={formData.type}
                        onChange={handleInputChange}
                        className="w-full px-3 py-2 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-all duration-200"
                        required
                      >
                       <option value="thu">Thu</option>
                       <option value="chi">Chi</option>
                     </select>
                   </div>
                   <div>
                     <label className="block text-sm font-semibold text-gray-700 mb-1">Loại tiền tệ</label>
                                        <select
                        name="currency"
                        value={formData.currency}
                        onChange={handleInputChange}
                        className="w-full px-3 py-2 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-all duration-200"
                        required
                      >
                                               <option value="VND">VND (VNĐ)</option>
                        <option value="USD">USD ($)</option>
                     </select>
                   </div>
                 </div>
                                 <div>
                   <label className="block text-sm font-semibold text-gray-700 mb-1">Ngày</label>
                                     <input
                     type="date"
                     name="date"
                     value={formData.date}
                     onChange={handleInputChange}
                     className="w-full px-3 py-2 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-all duration-200"
                     required
                   />
                </div>
                                 <div>
                   <label className="block text-sm font-semibold text-gray-700 mb-1">Số tiền</label>
                                     <input
                     type="text"
                     name="amount"
                     value={formData.amount}
                     onChange={handleInputChange}
                     className="w-full px-3 py-2 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-all duration-200"
                     placeholder="Nhập số tiền (VD: 1,000,000)"
                     required
                   />
                  {amountInWords && (
                    <div className="mt-2 p-3 bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-lg">
                      <p className="text-sm text-blue-700 font-medium">Bằng chữ:</p>
                      <p className="text-sm text-blue-800 italic font-semibold">{amountInWords}</p>
                    </div>
                  )}
                  {dynamicSuggestions.length > 0 && (
                    <div className="mt-2">
                      <span className="text-xs text-gray-500">Gợi ý:</span>
                      <div className="flex flex-wrap gap-2 mt-1">
                        {dynamicSuggestions.map((suggestion) => (
                          <button
                            key={suggestion}
                            type="button"
                            onClick={() => {
                              const currentCurrency = formData.currency || 'VND';
                              const formatted = formatNumberWithCommas(suggestion.toString(), currentCurrency);
                              setFormData(prev => ({ ...prev, amount: formatted }));
                              if (currentCurrency === 'USD') {
                                setAmountInWords(numberToEnglishText(suggestion));
                              } else {
                                setAmountInWords(numberToVietnameseText(suggestion));
                              }
                              setDynamicSuggestions([]); // Ẩn gợi ý sau khi chọn
                            }}
                            className="px-3 py-1 text-xs bg-gradient-to-r from-blue-50 to-indigo-50 hover:from-blue-100 hover:to-indigo-100 text-blue-700 border border-blue-200 rounded-lg transition-all duration-200 transform hover:scale-105"
                          >
                            {formatNumberWithCommas(suggestion.toString(), formData.currency || 'VND')}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                                     {dynamicSuggestions.length === 0 && formData.amount && (
                     <div className="mt-2">
                       <span className="text-xs text-gray-500">Gợi ý nhanh:</span>
                       <div className="flex gap-1 mt-1 overflow-x-auto">
                         {(formData.currency === 'USD' ? [0.25, 0.5, 1, 5, 10, 25, 50, 100] : [100000, 500000, 1000000, 5000000, 10000000]).map((suggestion) => (
                           <button
                             key={suggestion}
                             type="button"
                             onClick={() => {
                               const currentCurrency = formData.currency || 'VND';
                               const formatted = formatNumberWithCommas(suggestion.toString(), currentCurrency);
                               setFormData(prev => ({ ...prev, amount: formatted }));
                               if (currentCurrency === 'USD') {
                                 setAmountInWords(numberToEnglishText(suggestion));
                               } else {
                                 setAmountInWords(numberToVietnameseText(suggestion));
                               }
                             }}
                             className="px-2 py-1 text-xs bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-md transition-colors duration-200 whitespace-nowrap flex-shrink-0"
                           >
                             {formatNumberWithCommas(suggestion.toString(), formData.currency || 'VND')}
                           </button>
                         ))}
                       </div>
                     </div>
                   )}
                </div>
                                 <div>
                   <label className="block text-sm font-semibold text-gray-700 mb-1">Mô tả</label>
                                     <textarea
                     name="content"
                     value={formData.content}
                     onChange={handleInputChange}
                     className="w-full px-3 py-2 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-all duration-200"
                     rows="2"
                     placeholder="Nhập mô tả chi tiết"
                     required
                   />
                </div>

                                 <div className="flex justify-end space-x-4 pt-1">
                   <button
                     type="button"
                     onClick={() => setShowAddModal(false)}
                     className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-xl hover:bg-gray-200 transition-all duration-200"
                     disabled={loading}
                   >
                     Hủy
                   </button>
                   <button
                     type="submit"
                     className="px-4 py-2 text-sm font-medium text-white bg-gradient-to-r from-green-500 to-green-600 rounded-xl hover:from-green-600 hover:to-green-700 disabled:opacity-50 transition-all duration-200 transform hover:scale-105"
                     disabled={loading}
                   >
                     {loading ? 'Đang tạo...' : 'Thêm'}
                   </button>
                 </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {showEditModal && editingRecord && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            className="bg-white rounded-xl shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto"
          >
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-lg font-semibold text-gray-900">Sửa Thu Chi</h3>
                <button
                  onClick={() => setShowEditModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>

              <form onSubmit={handleSubmitEdit} className="space-y-4">
                {/* Type */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Loại</label>
                  <select
                    value={editingRecord.type}
                    onChange={(e) => setEditingRecord(prev => ({ ...prev, type: e.target.value }))}
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200"
                    required
                  >
                    <option value="income">Thu</option>
                    <option value="expense">Chi</option>
                  </select>
                </div>

                {/* Category */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Danh mục</label>
                  <select
                    value={editingRecord.category}
                    onChange={(e) => setEditingRecord(prev => ({ ...prev, category: e.target.value }))}
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200"
                    required
                  >
                    <option value="">Chọn danh mục</option>
                    {editingRecord.type === 'income' ? (
                      incomeCategories.map(category => (
                        <option key={category} value={category}>{category}</option>
                      ))
                    ) : (
                      expenseCategories.map(category => (
                        <option key={category} value={category}>{category}</option>
                      ))
                    )}
                  </select>
                </div>

                {/* Date */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Ngày</label>
                  <input
                    type="date"
                    value={editingRecord.date}
                    onChange={(e) => setEditingRecord(prev => ({ ...prev, date: e.target.value }))}
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200"
                    required
                  />
                </div>

                                 {/* Currency and Amount */}
                 <div className="grid grid-cols-2 gap-4">
                   <div>
                     <label className="block text-sm font-medium text-gray-700 mb-2">Tiền tệ</label>
                     <select
                       value={editingRecord.currency}
                       onChange={(e) => setEditingRecord(prev => ({ ...prev, currency: e.target.value }))}
                       className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200"
                       required
                     >
                       <option value="VND">VND</option>
                       <option value="USD">USD</option>
                     </select>
                   </div>
                   <div>
                     <label className="block text-sm font-medium text-gray-700 mb-2">Số tiền</label>
                     <input
                       type="text"
                       value={editingRecord.amount}
                       onChange={(e) => setEditingRecord(prev => ({ ...prev, amount: e.target.value }))}
                       placeholder="Nhập số tiền"
                       className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200"
                       required
                     />
                   </div>
                 </div>

                {/* Description */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Mô tả</label>
                  <textarea
                    value={editingRecord.description}
                    onChange={(e) => setEditingRecord(prev => ({ ...prev, description: e.target.value }))}
                    placeholder="Mô tả chi tiết..."
                    rows="3"
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200"
                    required
                  />
                </div>

                {/* Buttons */}
                <div className="flex space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={() => setShowEditModal(false)}
                    className="flex-1 px-4 py-3 border border-gray-300 text-gray-700 rounded-xl hover:bg-gray-50 transition-colors"
                  >
                    Hủy
                  </button>
                  <button
                    type="submit"
                    className="flex-1 px-4 py-3 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition-colors flex items-center justify-center"
                  >
                    <Save className="w-5 h-5 mr-2" />
                    Cập nhật
                  </button>
                </div>
              </form>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default AccountantAddFinance;
